import { plantHref } from './plant-surfaces'
import { isFigureCiteHref, parseCiteToken } from './cite-tokens'

/** Markdown link pattern: `[label](cite:scope:id)`. */
const CITE_LINK_PATTERN = /\[([^\]]+)\]\((cite:[^)]+)\)/g

/**
 * Convert every `cite:` markdown link to a real in-app href. Does not validate
 * ids against the graph — used client-side so Streamdown never sees blocked
 * custom protocols. All cite ids route through the entity resolver.
 */
export function resolveCiteLinksOptimistic(markdown: string): string {
  if (markdown.length === 0) return markdown
  return markdown.replace(CITE_LINK_PATTERN, (full, label: string, href: string) => {
    // Figure citations are resolved by ChatMarkdown against the current
    // conversation's ready, agent-authored attachments. Keep the token intact
    // here so the generic plant-link resolver does not reduce
    // `![caption](cite:figure:<id>)` to the malformed text `!caption`.
    if (isFigureCiteHref(href)) return full
    const parsed = parseCiteToken(href)
    if (parsed === null) return label
    return `[${label}](${plantHref(parsed.id)})`
  })
}

/**
 * Collect unique ontology ids from inline `[label](cite:scope:id)` markdown
 * links, preserving first-seen order. Invalid cite tokens are skipped.
 */
export function collectCiteIdsFromMarkdown(text: string): string[] {
  const ids: string[] = []
  CITE_LINK_PATTERN.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = CITE_LINK_PATTERN.exec(text)) !== null) {
    const parsed = parseCiteToken(match[2] ?? '')
    if (parsed !== null && !ids.includes(parsed.id)) ids.push(parsed.id)
  }
  return ids
}

/** True when `href` is a cite token (with or without markdown wrapper). */
export function isCiteHref(href: string): boolean {
  return href.startsWith('cite:')
}

/**
 * Normalize a link target for rendering. Cite tokens become plant entity
 * paths; other values pass through unchanged.
 */
export function normalizeLinkHref(href: string): string | null {
  if (!isCiteHref(href)) return href
  const parsed = parseCiteToken(href)
  if (parsed === null) return href
  return plantHref(parsed.id)
}
